import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { DashedTag } from '@praetorian-chariot/ui';
import { HourglassIcon, XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { getColumnWidth } from '@/components/table/columns/registry';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { TanStackTable } from '@/components/table/TanStackTable';
import { Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import { AgentJobsActions } from '@/sections/agents/components/actions/AgentJobsActions';
import { JobHistoryPaneProps } from '@/sections/agents/types/index';
import { Job, JobStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getJobStatus } from '@/utils/job';

import { useAgentJobs } from './useAgentJobs';

/**
 * Get status icon for job status
 */
const getStatusIcon = (status: JobStatus, animateRunning = true) => {
  switch (status) {
    case JobStatus.Running:
    case JobStatus.AsyncRunning:
      return (
        <ArrowPathIcon
          data-testid={
            status === JobStatus.AsyncRunning
              ? 'job-status-async-running'
              : 'job-status-running'
          }
          className={cn(
            'text-chariot-text-primary size-5',
            animateRunning && 'animate-spin'
          )}
        />
      );
    case JobStatus.Queued:
    case JobStatus.AsyncReturning:
      return (
        <HourglassIcon
          data-testid={
            status === JobStatus.AsyncReturning
              ? 'job-status-async-returning'
              : 'job-status-queued'
          }
          className="text-chariot-text-secondary size-5"
        />
      );
    case JobStatus.Pass:
      return (
        <CheckIcon
          data-testid="job-status-pass"
          className="text-chariot-stroke-info size-5"
        />
      );
    case JobStatus.Fail:
      return (
        <XIcon
          data-testid="job-status-fail"
          className="text-chariot-stroke-critical size-5"
        />
      );
    default:
      return null;
  }
};

/**
 * Component for displaying job history for a specific Aegis agent
 *
 * Provides:
 * - Job status indicators and actions
 * - Real-time updates when active
 * - Job rerun and cancellation capabilities
 * - Download functionality for completed jobs
 */
export const JobHistoryPane: React.FC<JobHistoryPaneProps> = ({
  agent,
  isActiveTab,
}) => {
  // Selection state for bulk actions
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Fetch agent-specific jobs with smart polling
  const {
    data: jobs,
    error: jobsError,
    status: jobsStatus,
  } = useAgentJobs(agent, undefined, {
    enabled: true,
    pollingEnabled: isActiveTab, // Only poll when this tab is active
  });

  // Compute selected jobs from selected row keys
  const selectedJobs = useMemo(() => {
    return selectedRows
      .map(key => jobs.find((job: Job) => job.key === key))
      .filter((job): job is NonNullable<typeof job> => job !== undefined);
  }, [selectedRows, jobs]);

  // Table columns definition - simplified for agent context
  // Removed: Source (redundant - we're in agent context)
  // Removed: Actions column (replaced by bulk actions)
  const columns: Columns<Job> = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        ...getColumnWidth('job', 'status'),
        cell: (job: Job) => getStatusIcon(getJobStatus(job)),
        align: 'center',
      },
      {
        id: 'source',
        label: 'Type',
        ...getColumnWidth('job', 'type'),
        cell: (job: Job) => <DashedTag size="xs" label={job.source} />,
      },
      {
        id: 'dns',
        label: 'Target',
        ...getColumnWidth('job', 'target'),
        cell: (job: Job) => {
          // Parse target hostname from job key (index 3)
          const targetHostname = job.key.split('#')[3];
          let target = job.dns;
          if (targetHostname && targetHostname !== target) {
            target = `${target} - ${targetHostname}`;
          }
          return target || '-';
        },
      },
      {
        id: 'updated',
        label: 'Updated',
        ...getColumnWidth('job', 'updated'),
        cell: 'date',
      },
    ],
    []
  );

  return (
    <div className="flex h-full flex-col gap-6" data-testid="job-history-pane">
      <Container
        className="border-chariot-stroke bg-chariot-background-primary flex h-full flex-1 flex-col rounded-md border p-6"
        data-testid="job-history-section"
      >
        <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
          {/* Header */}
          <div className="flex w-full items-start justify-between gap-4">
            <div className="flex flex-1 items-center gap-2">
              <h3 className="text-chariot-text-primary text-lg font-bold">
                Job History
              </h3>
              <Tooltip title="View execution history and results for jobs run on this agent.">
                <div className="text-chariot-text-secondary cursor-help">
                  <InformationCircleIcon className="size-4" />
                </div>
              </Tooltip>
            </div>
          </div>

          {/* Selection bar with bulk actions - appears when jobs selected */}
          {selectedRows.length > 0 && (
            <TableSelectionBar
              isVisible={selectedRows.length > 0}
              selectedCount={selectedJobs.length}
              totalCount={jobs.length}
              hasNextPage={false}
              resourceLabel="jobs"
              actions={
                <AgentJobsActions
                  jobs={selectedJobs}
                  selectedRowsCount={selectedRows.length}
                  onClearSelection={() => setSelectedRows([])}
                  disabled={selectedJobs.length === 0}
                />
              }
              data-testid="agent-jobs-selection-bar"
            />
          )}

          {/* Jobs Table */}
          <div className="border-chariot-stroke flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
            <TanStackTable<Job>
              name="agent-jobs"
              columns={columns}
              data={jobs}
              status={jobsStatus}
              error={jobsError}
              selection={{
                hide: false,
                value: selectedRows,
                onChange: setSelectedRows,
              }}
              noData={{
                title: 'No Jobs Found',
                description: `No jobs found for ${agent.hostname}. Jobs will appear here as scans are executed on this agent.`,
                icon: <ClipboardDocumentListIcon className="size-12" />,
              }}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};
